import { Style } from "@tecnobit-srl/styles-manager";
import "@tecnobit-srl/ui-spinner/css";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
//#region ../../styled-system/styled-system/helpers.js
function isObject(v) {
	return typeof v === "object" && v != null && !Array.isArray(v);
}
var HAS_OWN = Object.prototype.hasOwnProperty;
function isBaseCondition(v) {
	return v === "base";
}
function filterBaseConditions(c) {
	const out = [];
	for (let i = 0; i < c.length; i++) if (!isBaseCondition(c[i])) out.push(c[i]);
	return out;
}
function toHash(v) {
	let h = 5381;
	for (let i = v.length; i;) h = h * 33 ^ v.charCodeAt(--i);
	let x = h >>> 0, out = "";
	for (; x > 52; x = x / 52 | 0) {
		const c = x % 52;
		out = String.fromCharCode(c + (c > 25 ? 39 : 97)) + out;
	}
	const c = x % 52;
	return String.fromCharCode(c + (c > 25 ? 39 : 97)) + out;
}
function toResponsiveObject(values, breakpoints) {
	const out = {};
	for (let i = 0; i < values.length; i++) if (values[i] != null) out[breakpoints[i]] = values[i];
	return out;
}
function walkObject(target, fn, options) {
	options ||= {};
	const { stop, getKey } = options;
	const inner = (value, path = []) => {
		if (!value || typeof value !== "object") return fn(value, path);
		if (stop?.(value, path)) return fn(value, path);
		const out = Array.isArray(value) ? [] : {};
		for (const prop in value) {
			if (!HAS_OWN.call(value, prop) || prop === "__proto__") continue;
			const key = getKey?.(prop, value[prop]) ?? prop;
			path.push(key);
			const next = inner(value[prop], path);
			path.pop();
			if (next != null) out[key] = next;
		}
		return out;
	};
	return inner(target);
}
function normalizeStyleObject(styles, context, shorthand) {
	const { utility, conditions } = context;
	const { hasShorthand, resolveShorthand } = utility;
	shorthand = shorthand !== false;
	return walkObject(styles, (value) => {
		if (Array.isArray(value)) return toResponsiveObject(value, conditions.breakpoints.keys);
		return value;
	}, {
		stop: Array.isArray,
		getKey: shorthand ? (prop) => hasShorthand ? resolveShorthand(prop) : prop : void 0
	});
}
function flatHashOrNull(args) {
	let h = 5381;
	for (let a = 0; a < args.length; a++) {
		const obj = args[a];
		if (obj === null || typeof obj !== "object") {
			h = h * 33 ^ 1;
			continue;
		}
		for (const k in obj) {
			const v = obj[k];
			const tv = typeof v;
			if (v !== null && tv === "object") return null;
			for (let i = 0; i < k.length; i++) h = h * 33 ^ k.charCodeAt(i);
			if (tv === "string") for (let i = 0; i < v.length; i++) h = h * 33 ^ v.charCodeAt(i);
			else if (tv === "number") h = h * 33 ^ (v | 0);
			else if (tv === "boolean") h = h * 33 ^ (v ? 991 : 997);
			else h = h * 33 ^ 2;
		}
	}
	return h >>> 0;
}
function flatArgsEqual(a, b) {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		const oa = a[i];
		const ob = b[i];
		if (oa === ob) continue;
		if (oa === null || ob === null || typeof oa !== "object" || typeof ob !== "object") return false;
		let n = 0;
		for (const k in oa) {
			if (oa[k] !== ob[k]) return false;
			n++;
		}
		if (n !== Object.keys(ob).length) return false;
	}
	return true;
}
function memo(fn) {
	const cache = /* @__PURE__ */ new Map();
	const stringCache = /* @__PURE__ */ new Map();
	const seen = /* @__PURE__ */ new WeakSet();
	const newNode = () => ({
		objects: /* @__PURE__ */ new WeakMap(),
		prims: /* @__PURE__ */ new Map(),
		out: void 0,
		has: false
	});
	const root = newNode();
	let lastHash;
	let lastKey;
	let lastValue;
	let hasLast = false;
	let misses = 0;
	const step = (node, v) => {
		if (v !== null && typeof v === "object") {
			let next = node.objects.get(v);
			if (next === void 0) {
				next = newNode();
				node.objects.set(v, next);
			}
			return next;
		}
		let next = node.prims.get(v);
		if (next === void 0) {
			if (node.prims.size > 64) node.prims.clear();
			next = newNode();
			node.prims.set(v, next);
		}
		return next;
	};
	const walk = (node, v) => {
		if (Array.isArray(v)) {
			node = step(node, "\0[");
			for (let i = 0; i < v.length; i++) node = walk(node, v[i]);
			return step(node, "\0]");
		}
		return step(node, v);
	};
	const readWalk = (node, v) => {
		if (node === void 0) return void 0;
		if (Array.isArray(v)) {
			node = node.prims.get("\0[");
			for (let i = 0; i < v.length && node !== void 0; i++) node = readWalk(node, v[i]);
			return node === void 0 ? void 0 : node.prims.get("\0]");
		}
		return v !== null && typeof v === "object" ? node.objects.get(v) : node.prims.get(v);
	};
	const markSeen = (v) => {
		if (Array.isArray(v)) {
			let all = true;
			for (let i = 0; i < v.length; i++) if (!markSeen(v[i])) all = false;
			return all;
		}
		if (v === null || typeof v !== "object") return true;
		if (seen.has(v)) return true;
		seen.add(v);
		return false;
	};
	return ((...args) => {
		let composed = false;
		for (let i = 0; i < args.length; i++) if (Array.isArray(args[i])) {
			composed = true;
			break;
		}
		if (composed) {
			let node = root;
			for (let i = 0; i < args.length && node !== void 0; i++) node = readWalk(node, args[i]);
			if (node !== void 0 && node.has) return node.out;
			const composedKey = JSON.stringify(args);
			let composedOut = hasLast && lastHash === void 0 && composedKey === lastKey ? lastValue : stringCache.get(composedKey);
			if (composedOut === void 0) {
				composedOut = fn(...args);
				stringCache.set(composedKey, composedOut);
				if (stringCache.size > 500) stringCache.delete(stringCache.keys().next().value);
			}
			let reused = false;
			if ((++misses & 3) === 0) {
				reused = true;
				for (let i = 0; i < args.length; i++) if (!markSeen(args[i])) reused = false;
			}
			if (reused) {
				let insert = root;
				for (let i = 0; i < args.length; i++) insert = walk(insert, args[i]);
				insert.out = composedOut;
				insert.has = true;
			}
			lastHash = void 0;
			lastKey = composedKey;
			lastValue = composedOut;
			hasLast = true;
			return composedOut;
		}
		const hash = flatHashOrNull(args);
		if (hash !== null) {
			if (hasLast && lastHash === hash && flatArgsEqual(args, lastKey)) return lastValue;
			let bucket = cache.get(hash);
			if (bucket) {
				for (let i = 0; i < bucket.length; i++) if (flatArgsEqual(args, bucket[i].args)) {
					lastHash = hash;
					lastKey = args;
					lastValue = bucket[i].out;
					hasLast = true;
					return bucket[i].out;
				}
			}
			const out = fn(...args);
			if (!bucket) {
				bucket = [];
				cache.set(hash, bucket);
			}
			bucket.push({
				args,
				out
			});
			if (bucket.length > 8) bucket.shift();
			if (cache.size > 500) cache.delete(cache.keys().next().value);
			lastHash = hash;
			lastKey = args;
			lastValue = out;
			hasLast = true;
			return out;
		}
		const key = JSON.stringify(args);
		if (hasLast && lastHash === void 0 && key === lastKey) return lastValue;
		const cached = stringCache.get(key);
		if (cached !== void 0) {
			lastHash = void 0;
			lastKey = key;
			lastValue = cached;
			hasLast = true;
			return cached;
		}
		const out = fn(...args);
		stringCache.set(key, out);
		if (stringCache.size > 500) stringCache.delete(stringCache.keys().next().value);
		lastHash = void 0;
		lastKey = key;
		lastValue = out;
		hasLast = true;
		return out;
	});
}
function weakMemo(fn) {
	const cache = /* @__PURE__ */ new WeakMap();
	return ((arg) => {
		if (!arg || typeof arg !== "object") return fn(arg);
		if (cache.has(arg)) return cache.get(arg);
		const out = fn(arg);
		cache.set(arg, out);
		return out;
	});
}
function mergeProps(...src) {
	const out = {};
	for (const obj of src) {
		if (!obj) continue;
		for (const k in obj) {
			if (!HAS_OWN.call(obj, k) || k === "__proto__" || k === "constructor" || k === "prototype") continue;
			const prev = out[k];
			const next = obj[k];
			out[k] = isObject(prev) && isObject(next) ? mergeProps(prev, next) : next;
		}
	}
	return out;
}
var WHITESPACE_REGEX = /[\n\s]+/g;
var sanitizeStyleValue = (value) => typeof value === "string" ? value.replace(WHITESPACE_REGEX, " ") : value;
function resolveStyleArgs(styles, context) {
	const out = [];
	const visit = (items) => {
		for (let i = 0; i < items.length; i++) {
			const style = items[i];
			if (Array.isArray(style)) {
				visit(style);
				continue;
			}
			if (!isObject(style)) continue;
			for (const key in style) if (style[key] !== void 0) {
				out.push(style);
				break;
			}
		}
	};
	visit(styles);
	if (out.length < 2) return out;
	for (let i = 0; i < out.length; i++) out[i] = normalizeStyleObject(out[i], context);
	return out;
}
function createSerializeCss(context) {
	const u = context.utility;
	const c = context.conditions;
	const hash = context.hash;
	const fmt = (s) => u.prefix ? u.prefix + "-" + s : s;
	const toClass = (paths, name) => {
		const parts = c.finalize(paths);
		parts.push(hash ? name : fmt(name));
		return hash ? fmt(u.toHash(parts, toHash)) : parts.join(":");
	};
	return weakMemo(memo(function serializeCss({ base, ...styles } = {}) {
		const obj = normalizeStyleObject(base ? Object.assign(styles, base) : styles, context);
		const set = /* @__PURE__ */ new Set();
		walkObject(obj, (value, paths) => {
			if (value == null) return;
			const important = isImportant(value);
			const [prop, ...all] = c.shift(paths);
			const cond = filterBaseConditions(all);
			const res = u.transform(prop, withoutSpace(withoutImportant(sanitizeStyleValue(value))));
			let name = toClass(cond, res.className);
			if (important) name += "!";
			set.add(name);
		});
		let out = "";
		for (const name of set) out += out ? " " + name : name;
		return out;
	}));
}
function createMergeCss(context) {
	return function mergeCss() {
		return mergeProps(...resolveStyleArgs(arguments, context));
	};
}
function createSerializeCssArgs(serializeCss, mergeCss) {
	return memo(function serializeCssArgs(...styles) {
		return serializeCss(mergeCss(...styles));
	});
}
var HYPHENATE_PROPERTY_REGEX = /[A-Z]/g;
var MS_PROPERTY_REGEX = /^ms-/;
function hypenateProperty(property) {
	return property.startsWith("--") ? property : property.replace(HYPHENATE_PROPERTY_REGEX, "-$&").replace(MS_PROPERTY_REGEX, "-ms-").toLowerCase();
}
function withoutSpace(str) {
	return typeof str === "string" && str.indexOf(" ") >= 0 ? str.replaceAll(" ", "_") : str;
}
var IMPORTANT_REGEX = /\s*!(important)?\s*$/i;
function isImportant(value) {
	return typeof value === "string" ? IMPORTANT_REGEX.test(value) : false;
}
function withoutImportant(value) {
	return typeof value === "string" ? value.replace(IMPORTANT_REGEX, "").trim() : value;
}
//#endregion
//#region ../../styled-system/styled-system/css/conditions.js
var conditions = /* @__PURE__ */ new Set(/* @__PURE__ */ "2xl,2xlDown,2xlOnly,2xlToDesktop,2xlToTablet,2xlToXs,@/2xl,@/2xlDown,@/2xlOnly,@/2xlTo3xl,@/2xlTo4xl,@/2xlTo5xl,@/2xlTo6xl,@/2xlTo7xl,@/2xlTo8xl,@/2xs,@/2xsDown,@/2xsOnly,@/2xsTo2xl,@/2xsTo3xl,@/2xsTo4xl,@/2xsTo5xl,@/2xsTo6xl,@/2xsTo7xl,@/2xsTo8xl,@/2xsToLg,@/2xsToMd,@/2xsToSm,@/2xsToXl,@/2xsToXs,@/3xl,@/3xlDown,@/3xlOnly,@/3xlTo4xl,@/3xlTo5xl,@/3xlTo6xl,@/3xlTo7xl,@/3xlTo8xl,@/3xs,@/3xsDown,@/3xsOnly,@/3xsTo2xl,@/3xsTo2xs,@/3xsTo3xl,@/3xsTo4xl,@/3xsTo5xl,@/3xsTo6xl,@/3xsTo7xl,@/3xsTo8xl,@/3xsToLg,@/3xsToMd,@/3xsToSm,@/3xsToXl,@/3xsToXs,@/4xl,@/4xlDown,@/4xlOnly,@/4xlTo5xl,@/4xlTo6xl,@/4xlTo7xl,@/4xlTo8xl,@/5xl,@/5xlDown,@/5xlOnly,@/5xlTo6xl,@/5xlTo7xl,@/5xlTo8xl,@/6xl,@/6xlDown,@/6xlOnly,@/6xlTo7xl,@/6xlTo8xl,@/7xl,@/7xlDown,@/7xlOnly,@/7xlTo8xl,@/8xl,@/8xlDown,@/8xlOnly,@/lg,@/lgDown,@/lgOnly,@/lgTo2xl,@/lgTo3xl,@/lgTo4xl,@/lgTo5xl,@/lgTo6xl,@/lgTo7xl,@/lgTo8xl,@/lgToXl,@/md,@/mdDown,@/mdOnly,@/mdTo2xl,@/mdTo3xl,@/mdTo4xl,@/mdTo5xl,@/mdTo6xl,@/mdTo7xl,@/mdTo8xl,@/mdToLg,@/mdToXl,@/sm,@/smDown,@/smOnly,@/smTo2xl,@/smTo3xl,@/smTo4xl,@/smTo5xl,@/smTo6xl,@/smTo7xl,@/smTo8xl,@/smToLg,@/smToMd,@/smToXl,@/xl,@/xlDown,@/xlOnly,@/xlTo2xl,@/xlTo3xl,@/xlTo4xl,@/xlTo5xl,@/xlTo6xl,@/xlTo7xl,@/xlTo8xl,@/xs,@/xsDown,@/xsOnly,@/xsTo2xl,@/xsTo3xl,@/xsTo4xl,@/xsTo5xl,@/xsTo6xl,@/xsTo7xl,@/xsTo8xl,@/xsToLg,@/xsToMd,@/xsToSm,@/xsToXl,_active,_after,_anyPointerCoarse,_anyPointerFine,_anyPointerNone,_atValue,_autofill,_backdrop,_before,_checked,_closed,_complete,_current,_currentPage,_currentStep,_dark,_default,_disabled,_dragging,_empty,_enabled,_even,_expanded,_file,_first,_firstLetter,_firstLine,_firstOfType,_focus,_focusVisible,_focusWithin,_fullscreen,_grabbed,_groupActive,_groupChecked,_groupDisabled,_groupExpanded,_groupFocus,_groupFocusVisible,_groupFocusWithin,_groupHover,_groupInvalid,_hidden,_highContrast,_highlighted,_horizontal,_hover,_icon,_inRange,_incomplete,_indeterminate,_inert,_invalid,_invertedColors,_landscape,_last,_lastOfType,_lessContrast,_light,_loading,_ltr,_marker,_moreContrast,_motionReduce,_motionSafe,_noscript,_now,_odd,_only,_onlyOfType,_open,_optional,_osDark,_osLight,_outOfRange,_overValue,_peerActive,_peerChecked,_peerDisabled,_peerExpanded,_peerFocus,_peerFocusVisible,_peerFocusWithin,_peerHover,_peerInvalid,_peerPlaceholderShown,_placeholder,_placeholderShown,_pointerCoarse,_pointerFine,_pointerNone,_portrait,_pressed,_print,_rangeEnd,_rangeStart,_readOnly,_readWrite,_required,_rtl,_scrollbar,_scrollbarThumb,_scrollbarTrack,_selected,_selection,_starting,_target,_today,_topmost,_unavailable,_underValue,_userInvalid,_userValid,_valid,_vertical,_visited,base,desktop,desktopDown,desktopOnly,lg,lgDown,lgOnly,lgTo2xl,lgToDesktop,lgToTablet,lgToXl,lgToXs,md,mdDown,mdOnly,mdTo2xl,mdToDesktop,mdToLg,mdToTablet,mdToXl,mdToXs,sm,smDown,smOnly,smTo2xl,smToDesktop,smToLg,smToMd,smToTablet,smToXl,smToXs,tablet,tabletDown,tabletOnly,tabletToDesktop,xl,xlDown,xlOnly,xlTo2xl,xlToDesktop,xlToTablet,xlToXs,xs,xsDown,xsOnly,xsToDesktop,xsToTablet".split(","));
var conditionRe = /^@|&/;
var underscoreRe = /^_/;
var selectorRe = /&|@/;
var breakpointKeys = [
	"base",
	"sm",
	"md",
	"lg",
	"xl",
	"2xl",
	"xs",
	"tablet",
	"desktop"
];
function isCondition(v) {
	return conditions.has(v) || conditionRe.test(v);
}
function finalizeConditions(paths) {
	return paths.map((p) => {
		if (conditions.has(p)) return p.replace(underscoreRe, "");
		if (selectorRe.test(p)) return `[${withoutSpace(p.trim())}]`;
		return p;
	});
}
function sortConditions(paths) {
	return [...paths].sort((a, b) => {
		const aa = isCondition(a);
		const bb = isCondition(b);
		return aa && !bb ? 1 : !aa && bb ? -1 : 0;
	});
}
//#endregion
//#region ../../styled-system/styled-system/css/css.js
var utilities = "WebkitTextFillColor:wktf-c,accentColor:ac-c,alignContent:ac,alignItems:ai,alignSelf:as,animation:anim,animationComposition:anim-comp,animationDelay:anim-dly,animationDirection:anim-dir,animationDuration:anim-dur,animationFillMode:anim-fm,animationIterationCount:anim-ic,animationName:anim-n,animationPlayState:anim-ps,animationRange:anim-r,animationRangeEnd:anim-re,animationRangeStart:anim-rs,animationState:anim-s,animationTimeline:anim-tl,animationTimingFunction:anim-tmf,appearance:ap,aspectRatio:asp,backdropBlur:bkdp-blur,backdropBrightness:bkdp-brightness,backdropContrast:bkdp-contrast,backdropFilter:bkdp,backdropGrayscale:bkdp-grayscale,backdropHueRotate:bkdp-hue-rotate,backdropInvert:bkdp-invert,backdropOpacity:bkdp-opacity,backdropSaturate:bkdp-saturate,backdropSepia:bkdp-sepia,backfaceVisibility:bfv,background:bg/1,backgroundAttachment:bg-a/bgAttachment,backgroundBlendMode:bg-bm/bgBlendMode,backgroundClip:bg-cp/bgClip,backgroundColor:bg-c/bgColor,backgroundConic:bg-conic/bgConic,backgroundGradient:bg-grad/bgGradient,backgroundImage:bg-i/bgImage,backgroundLinear:bg-linear/bgLinear,backgroundOrigin:bg-o/bgOrigin,backgroundPosition:bg-p/bgPosition,backgroundPositionX:bg-p-x/bgPositionX,backgroundPositionY:bg-p-y/bgPositionY,backgroundRadial:bg-radial/bgRadial,backgroundRepeat:bg-r/bgRepeat,backgroundSize:bg-s/bgSize,blockSize:h-bs,border:bd,borderBlock:bd-y/borderY,borderBlockColor:bd-y-c/borderYColor,borderBlockEnd:bd-be,borderBlockEndColor:bd-be-c,borderBlockEndWidth:bd-be-w,borderBlockStart:bd-bs,borderBlockStartColor:bd-bs-c,borderBlockStartWidth:bd-bs-w,borderBlockWidth:bd-y-w/borderYWidth,borderBottom:bd-b,borderBottomColor:bd-b-c,borderBottomLeftRadius:bdr-bl/roundedBottomLeft,borderBottomRadius:bdr-b/roundedBottom,borderBottomRightRadius:bdr-br/roundedBottomRight,borderBottomWidth:bd-b-w,borderCollapse:bd-cl,borderColor:bd-c,borderEndEndRadius:bdr-ee/roundedEndEnd,borderEndRadius:bdr-e/roundedEnd,borderEndStartRadius:bdr-es/roundedEndStart,borderInline:bd-x/borderX,borderInlineColor:bd-x-c/borderXColor,borderInlineEnd:bd-e/borderEnd,borderInlineEndColor:bd-e-c/borderEndColor,borderInlineEndWidth:bd-e-w/borderEndWidth,borderInlineStart:bd-s/borderStart,borderInlineStartColor:bd-s-c/borderStartColor,borderInlineStartWidth:bd-s-w/borderStartWidth,borderInlineWidth:bd-x-w/borderXWidth,borderLeft:bd-l,borderLeftColor:bd-l-c,borderLeftRadius:bdr-l/roundedLeft,borderLeftWidth:bd-l-w,borderRadius:bdr/rounded,borderRight:bd-r,borderRightColor:bd-r-c,borderRightRadius:bdr-r/roundedRight,borderRightWidth:bd-r-w,borderSpacing:bd-sp,borderSpacingX:bd-sx,borderSpacingY:bd-sy,borderStartEndRadius:bdr-se/roundedStartEnd,borderStartRadius:bdr-s/roundedStart,borderStartStartRadius:bdr-ss/roundedStartStart,borderTop:bd-t,borderTopColor:bd-t-c,borderTopLeftRadius:bdr-tl/roundedTopLeft,borderTopRadius:bdr-t/roundedTop,borderTopRightRadius:bdr-tr/roundedTopRight,borderTopWidth:bd-t-w,borderWidth:bd-w,boxDecorationBreak:bx-db,boxShadow:bx-sh/shadow,boxShadowColor:bx-sh-c/shadowColor,boxSize:size,boxSizing:bx-s,caretColor:ca-c,clipPath:cp-path,color:c,columnGap:cg,container:cq,containerName:cq-n,containerType:cq-t,display:d,divideColor:dvd-c,divideStyle:dvd-s,divideX:dvd-x,divideY:dvd-y,flexBasis:flex-b,flexDirection:flex-d/flexDir,flexGrow:flex-g,flexShrink:flex-sh,focusRingColor:focus-ring-c,focusRingOffset:focus-ring-o,focusRingStyle:focus-ring-s,focusRingWidth:focus-ring-w,focusVisibleRing:focus-v-ring,fontFamily:ff,fontFeatureSettings:ff-s,fontKerning:fk,fontPalette:fp,fontSize:fs,fontSizeAdjust:fs-a,fontSmoothing:fsmt,fontVariant:fv,fontVariantAlternates:fv-alt,fontVariantCaps:fv-caps,fontVariantNumeric:fv-num,fontVariationSettings:fv-s,fontWeight:fw,gradientFrom:grad-from,gradientFromPosition:grad-from-pos,gradientTo:grad-to,gradientToPosition:grad-to-pos,gradientVia:grad-via,gradientViaPosition:grad-via-pos,gridAutoColumns:grid-ac,gridAutoFlow:grid-af,gridAutoRows:grid-ar,gridColumn:grid-c,gridColumnEnd:grid-ce,gridColumnGap:grid-cg,gridColumnStart:grid-cs,gridGap:grid-g,gridRow:grid-r,gridRowGap:grid-rg,gridTemplateColumns:grid-tc,gridTemplateRows:grid-tr,height:h/1,hideBelow:show,hideFrom:hide,hyphens:hy,inlineSize:w-is,insetBlock:inset-y/insetY,insetBlockEnd:inset-be,insetBlockStart:inset-bs,insetInline:inset-x/insetX,insetInlineEnd:inset-e/end/insetEnd,insetInlineStart:inset-s/insetStart/start,justifyContent:jc,letterSpacing:ls,lineClamp:lc,lineHeight:lh,listStyle:li-s,listStyleImage:li-img,listStylePosition:li-pos,listStyleType:li-t,margin:m/1,marginBlock:my/marginY/1,marginBlockEnd:mbe,marginBlockStart:mbs,marginBottom:mb/1,marginInline:mx/marginX/1,marginInlineEnd:me/marginEnd/1,marginInlineStart:ms/marginStart/1,marginLeft:ml/1,marginRight:mr/1,marginTop:mt/1,mask:msk,maskBottomFrom:msk-b-from,maskBottomFromColor:msk-b-from-c,maskBottomTo:msk-b-to,maskBottomToColor:msk-b-to-c,maskClip:msk-cp,maskComposite:msk-cmp,maskConic:msk-conic,maskConicFrom:msk-conic-from,maskConicFromColor:msk-conic-from-c,maskConicTo:msk-conic-to,maskConicToColor:msk-conic-to-c,maskImage:msk-i,maskLeftFrom:msk-l-from,maskLeftFromColor:msk-l-from-c,maskLeftTo:msk-l-to,maskLeftToColor:msk-l-to-c,maskLinear:msk-linear,maskLinearFrom:msk-linear-from,maskLinearFromColor:msk-linear-from-c,maskLinearTo:msk-linear-to,maskLinearToColor:msk-linear-to-c,maskMode:msk-md,maskOrigin:msk-o,maskPosition:msk-p,maskRadial:msk-radial,maskRadialAt:msk-radial-at,maskRadialFrom:msk-radial-from,maskRadialFromColor:msk-radial-from-c,maskRadialShape:msk-radial-shape,maskRadialSize:msk-radial-sz,maskRadialTo:msk-radial-to,maskRadialToColor:msk-radial-to-c,maskRepeat:msk-r,maskRightFrom:msk-r-from,maskRightFromColor:msk-r-from-c,maskRightTo:msk-r-to,maskRightToColor:msk-r-to-c,maskSize:msk-s,maskTopFrom:msk-t-from,maskTopFromColor:msk-t-from-c,maskTopTo:msk-t-to,maskTopToColor:msk-t-to-c,maskType:msk-t,maskXFrom:msk-x-from,maskXFromColor:msk-x-from-c,maskXTo:msk-x-to,maskXToColor:msk-x-to-c,maskYFrom:msk-y-from,maskYFromColor:msk-y-from-c,maskYTo:msk-y-to,maskYToColor:msk-y-to-c,maxBlockSize:max-b,maxHeight:max-h/maxH,maxInlineSize:max-w-is,maxWidth:max-w/maxW,minBlockSize:min-h-bs,minHeight:min-h/minH,minInlineSize:min-w-is,minWidth:min-w/minW,mixBlendMode:mix-bm,objectFit:obj-f,objectPosition:obj-p,opacity:op,outline:ring/1,outlineColor:ring-c/ringColor,outlineOffset:ring-o/ringOffset,outlineWidth:ring-w/ringWidth,overflow:ov,overflowAnchor:ov-a,overflowBlock:ov-b,overflowClipBox:ovcp-bx,overflowClipMargin:ovcp-m,overflowInline:ov-i,overflowWrap:ov-wrap,overflowX:ov-x,overflowY:ov-y,overscrollBehavior:ovs-b,overscrollBehaviorBlock:ovs-bb,overscrollBehaviorInline:ovs-bi,overscrollBehaviorX:ovs-bx,overscrollBehaviorY:ovs-by,padding:p/1,paddingBlock:py/paddingY/1,paddingBlockEnd:pbe,paddingBlockStart:pbs,paddingBottom:pb/1,paddingInline:px/paddingX/1,paddingInlineEnd:pe/paddingEnd/1,paddingInlineStart:ps/paddingStart/1,paddingLeft:pl/1,paddingRight:pr/1,paddingTop:pt/1,position:pos/1,rowGap:rg,scrollBehavior:scr-bhv,scrollMargin:scr-m,scrollMarginBlock:scr-my/scrollMarginY,scrollMarginBlockEnd:scr-mbe,scrollMarginBlockStart:scr-mbt,scrollMarginBottom:scr-mb,scrollMarginInline:scr-mx/scrollMarginX,scrollMarginInlineEnd:scr-me,scrollMarginInlineStart:scr-ms,scrollMarginLeft:scr-ml,scrollMarginRight:scr-mr,scrollMarginTop:scr-mt,scrollPadding:scr-p,scrollPaddingBlock:scr-py/scrollPaddingY,scrollPaddingBlockEnd:scr-pbe,scrollPaddingBlockStart:scr-pbs,scrollPaddingBottom:scr-pb,scrollPaddingInline:scr-px/scrollPaddingX,scrollPaddingInlineEnd:scr-pe,scrollPaddingInlineStart:scr-ps,scrollPaddingLeft:scr-pl,scrollPaddingRight:scr-pr,scrollPaddingTop:scr-pt,scrollSnapAlign:scr-sa,scrollSnapCoordinate:scrs-c,scrollSnapDestination:scrs-d,scrollSnapMargin:scrs-m,scrollSnapMarginBottom:scrs-mb,scrollSnapMarginLeft:scrs-ml,scrollSnapMarginRight:scrs-mr,scrollSnapMarginTop:scrs-mt,scrollSnapPointsX:scrs-px,scrollSnapPointsY:scrs-py,scrollSnapStop:scrs-s,scrollSnapStrictness:scrs-strt,scrollSnapType:scrs-t,scrollSnapTypeX:scrs-tx,scrollSnapTypeY:scrs-ty,scrollTimeline:scrtl,scrollTimelineAxis:scrtl-a,scrollTimelineName:scrtl-n,scrollbar:scr-bar,scrollbarColor:scr-bar-c,scrollbarGutter:scr-bar-g,scrollbarThumb:scr-bar-th,scrollbarTrack:scr-bar-tk,scrollbarWidth:scr-bar-w,spaceX:sx,spaceY:sy,srOnly:sr,stroke:stk,strokeDasharray:stk-dsh,strokeDashoffset:stk-do,strokeLinecap:stk-lc,strokeLinejoin:stk-lj,strokeMiterlimit:stk-ml,strokeOpacity:stk-op,strokeWidth:stk-w,tableLayout:tbl,textAlign:ta,textDecoration:td,textDecorationColor:td-c,textDecorationStyle:td-s,textDecorationThickness:td-t,textEmphasisColor:te-c,textGradient:txt-grad,textIndent:ti,textOverflow:tov,textShadow:tsh,textShadowColor:tsh-c/textShadowColor,textSizeAdjust:txt-adj,textStyle:textStyle,textTransform:tt,textUnderlineOffset:tu-o,textWrap:tw,touchAction:tch-a,transform:trf,transformBox:trf-b,transformOrigin:trf-o,transformStyle:trf-s,transition:trs,transitionDelay:trs-dly,transitionDuration:trs-dur,transitionProperty:trs-prop,transitionTimingFunction:trs-tmf,translateX:/x,translateY:/y,translateZ:/z,truncate:trunc,userSelect:us,verticalAlign:va,visibility:vis,width:w/1,wordBreak:wb,zIndex:z";
function createCssContext() {
	const classNameByProp = /* @__PURE__ */ new Map();
	const shorthands = /* @__PURE__ */ new Map();
	utilities.split(",").forEach((utility) => {
		const [prop, meta] = utility.split(":");
		const [className, ...shorthandList] = meta.split("/");
		if (className) classNameByProp.set(prop, className);
		shorthandList.forEach((shorthand) => {
			const key = shorthand === "1" ? className : shorthand;
			shorthands.set(key, prop);
		});
	});
	const resolveShorthand = (prop) => shorthands.get(prop) || prop;
	return {
		hash: true,
		conditions: {
			shift: sortConditions,
			finalize: finalizeConditions,
			breakpoints: { keys: breakpointKeys }
		},
		utility: {
			prefix: null,
			hasShorthand: true,
			toHash(path, hashFn) {
				return hashFn(path.join(":"));
			},
			transform(prop, value) {
				const key = resolveShorthand(prop);
				return { className: `${classNameByProp.get(key) || hypenateProperty(key)}_${withoutSpace(value)}` };
			},
			resolveShorthand
		}
	};
}
var cssContext = /* @__PURE__ */ createCssContext();
var serializeCss = /* @__PURE__ */ createSerializeCss(cssContext);
var mergeCss = /* @__PURE__ */ createMergeCss(cssContext);
var serializeCssArgs = /* @__PURE__ */ createSerializeCssArgs(serializeCss, mergeCss);
var css = /* @__PURE__ */ Object.assign(function css(...styles) {
	if (styles.length === 1 && isObject(styles[0])) return serializeCss(styles[0]);
	return serializeCssArgs(...styles);
}, { raw: function cssRaw(...styles) {
	return mergeCss(...styles);
} });
//#endregion
//#region ../../styled-system/styled-system/css/cx.js
/**
* Conditionally join classNames into a single string
*/
function cx(...args) {
	let str = "", i = 0, arg;
	for (; i < arguments.length;) if ((arg = arguments[i++]) && typeof arg === "string") {
		str && (str += " ");
		str += arg;
	}
	return str;
}
//#endregion
//#region src/spinner.tsx
function Spinner({ color, size, css: cssFromProp }) {
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(Style, { style: "core-ui/spinner" }), /* @__PURE__ */ jsx("div", {
		className: cx("spinner", size, css(cssFromProp)),
		"data-color": color ?? "accent"
	})] });
}
//#endregion
export { Spinner };
